import type {ProblemMinimal, Rank} from '@types';
import {
    FIRST_DAN_LEVEL,
    LAST_KNOWN_LEVEL,
    LAST_KNOWN_KYU_LEVEL,
    ELO_LEVEL_BASE,
    ELO_LEVEL_GAP,
    ELO_LOW_LEVEL_COUNT,
    ELO_LEVEL_GAP_LOW,
    ELO_LEVEL_6_DAN,
    ELO_LEVEL_7_DAN,
    ELO_LEVEL_8_DAN,
    ELO_LEVEL_9_DAN,
    ELO_LEVEL_DEFAULT_FOR_USER,
} from './const/level';
import {
    POST_9_DAN_MIDPOINT_ELO,
    POST_9_DAN_STEEPNESS_FACTOR,
} from './const/elo';

export const calculateEloLevelName = (elo: number, precision = false) => {
    let level;
    if (precision) {
        level = convertEloToLevelWithPrecision(elo);
    } else {
        level = convertEloToLevel(elo);
    }
    return getLevelName(level, precision);
};

export const calculateRoundedEloLevelName = (elo: number) => {
    const level = convertEloToLevelWithPrecision(elo);
    const roundedLevel =
        level < FIRST_DAN_LEVEL
            ? Math.min(
                  FIRST_DAN_LEVEL - Math.round(FIRST_DAN_LEVEL - level),
                  LAST_KNOWN_KYU_LEVEL
              )
            : Math.min(Math.round(level), LAST_KNOWN_LEVEL);

    return getLevelName(roundedLevel);
};

export const calculateEloShortLevelName = (elo: number) => {
    return getShortLevelName(convertEloToLevel(elo));
};

const getLevelValue = (level: number, precision = false) => {
    const flooredLevel = Math.floor(level);
    const precisionValue = level - flooredLevel;
    let value: number | string =
        flooredLevel < 30 ? 30 - flooredLevel : flooredLevel - 29;
    if (precision) {
        const fractionalValue =
            flooredLevel < FIRST_DAN_LEVEL ? -precisionValue : precisionValue;
        // 1 kyu is the strongest kyu rank until the 1 dan cutoff is reached.
        value = Math.max(1, value + fractionalValue).toFixed(2);
    } else {
        value = Math.floor(value);
    }
    return value;
};

export const calculateEloLevelDelta = (elo: number, previousElo: number) => {
    // Compare the displayed ranks so rounding cannot hide a visible change.
    const displayedLevel = (rating: number) => {
        const level = convertEloToLevelWithPrecision(rating);
        const value = Number(getLevelValue(level, true));
        return level < FIRST_DAN_LEVEL ? -value : value - 1;
    };

    return Number((displayedLevel(elo) - displayedLevel(previousElo)).toFixed(2));
};

export const getLevelName = (level: number, precision = false) => {
    const value = getLevelValue(level, precision);
    return `${value} ${level < 30 ? 'kyu' : 'dan'}`;
};

export const getLevelNameShort = (level: number, precision = false) => {
    const value = getLevelValue(level, precision);
    return `${value} ${level < 30 ? 'k' : 'd'}`;
};

export const mobileCheck = function () {
    const isSmallScreen = window.screen.width < 768;
    const isMobileMediaQuery = window.matchMedia('(max-width: 768px)').matches;

    return isSmallScreen && isMobileMediaQuery;
};

export const getShortLevelName = (level: number): string => {
    let value;
    if (level < FIRST_DAN_LEVEL) {
        value = FIRST_DAN_LEVEL - level;
    } else {
        value = level - LAST_KNOWN_KYU_LEVEL;
    }

    return level < FIRST_DAN_LEVEL ? `${value}k` : `${value}d`;
};

export const getShortLevelNameFromRank = (rank?: Rank) => {
    if (!rank) return '';
    return rank.value + `${rank.unit.toString().charAt(0)}`;
};

export function getEloLevelCutoffs() {
    const cutoffs = [];
    cutoffs.push(0); // 30k

    for (let level = 1; level < 39; level++) {
        // basic calculation is a linear function on level
        let elo = ELO_LEVEL_BASE + level * ELO_LEVEL_GAP;

        // handle low levels specially, to spread out a bit
        if (level < ELO_LOW_LEVEL_COUNT) {
            let lowBase =
                ELO_LEVEL_BASE - ELO_LOW_LEVEL_COUNT * ELO_LEVEL_GAP_LOW;
            lowBase += ELO_LEVEL_GAP * ELO_LOW_LEVEL_COUNT;
            elo = lowBase + level * ELO_LEVEL_GAP_LOW;
        }

        cutoffs.push(elo);
    }

    // override known values
    cutoffs[30 - 1 + 6] = ELO_LEVEL_6_DAN;
    cutoffs[30 - 1 + 7] = ELO_LEVEL_7_DAN;
    cutoffs[30 - 1 + 8] = ELO_LEVEL_8_DAN;
    cutoffs[30 - 1 + 9] = ELO_LEVEL_9_DAN;

    return cutoffs;
}

export function convertLevelToElo(level: number): number {
    const cutoffs = getEloLevelCutoffs();
    return cutoffs[level];
}
export const getProblemEloFromProblemRank = (problem: ProblemMinimal) => {
    const rank = problem?.rank;
    if (!rank) return null;

    if (rank.unit === 'kyu') {
        return convertLevelToElo(30 - rank.value);
    }

    if (rank.unit === 'dan') {
        return convertLevelToElo(rank.value + 29);
    }

    return null;
};

export const convertEloToLevel = (elo: number) => {
    const cutoffs = getEloLevelCutoffs();

    for (let level = 38; level >= 1; level--) {
        if (elo >= cutoffs[level]) {
            return level;
        }
    }

    return 0; // 30k catches all the low end
};

const convertEloToContinuousLevel = (elo: number) => {
    const cutoffs = getEloLevelCutoffs();

    for (let level = 38; level >= 1; level--) {
        if (elo === cutoffs[level]) {
            return level;
        }
        if (elo > cutoffs[level]) {
            let precision = 0;

            if (level < 38) {
                precision =
                    ((elo - cutoffs[level]) /
                        (cutoffs[level + 1] - cutoffs[level])) *
                    100;
            } else {
                // maximum rating can be 9.99 dan
                precision = Math.min(
                    99.0,
                    100.0 /
                        (1.0 +
                            Math.exp(
                                -POST_9_DAN_STEEPNESS_FACTOR *
                                    (elo - POST_9_DAN_MIDPOINT_ELO)
                            ))
                );
            }

            return level + precision / 100;
        }
    }

    return 0; // 30k catches all the low end
};

export const convertEloToLevelWithPrecision = (elo: number) => {
    return convertEloToContinuousLevel(elo);
};

// Keep chart positions continuous so exact rank cutoffs land on their ticks.
// Kyu tick labels count down as the chart coordinate increases.
export const convertEloToChartLevel = (elo: number) => {
    return convertEloToContinuousLevel(elo);
};

export const isDefaultProblemElo = (elo: number) => {
    return elo === ELO_LEVEL_DEFAULT_FOR_USER;
};
